<template>
  <div>
    <intent-examples-header :intent-examples="answerExamples"
                            :selected-examples-count="selectedExamplesCount"
                            :is-bulk-move-ready="!!targetIntent"
                            :mode="mode"
                            @update-mode="value => mode = value"
                            @delete-examples="deleteExamples"
                            @move-examples="moveExamples"
                            @select-all="selectAll"
                            @deselect-all="deselectAll"
    />

    <div class="mt-4 min-h-0">
      <transition name="slide-fade-y">
        <div v-if="isEmptyAnswerExamples && mode === EXAMPLE_EDITOR_MODE.ADD" class="text-gray">
          Add examples of requests your customers might ask.<br/>
          1. Fill the input with an example<br/>
          2. Hit the "Enter" key to submit
        </div>
      </transition>

      <with-loader v-if="mode === EXAMPLE_EDITOR_MODE.ADD"
                   id="example-input"
                   :loading="exampleSubmitted"
                   class="h-full pr-6 mb-4"
                   :class="{'mt-2': isEmptyAnswerExamples}"
                   use-as-overlay
      >
        <base-input placeholder="Type or paste example"
                    class="w-full"
                    v-model.trim="newExample"
                    ref="newExampleInput"
                    @keydown.esc="handleEscapeKey"
                    @keydown.enter="submitNewExample"
                    :disabled="exampleSubmitted"
        />
        <span class="text-gray text-xs">Press ENTER to save</span>
      </with-loader>

      <answer-select v-if="mode === EXAMPLE_EDITOR_MODE.BULK_MOVE"
                     class="intent-suggestion-examples shadow-card rounded h-full border-transparent mt-2"
                     :answer-options="answerOptions"
                     :intent-platform-id="suggestedIntent"
                     @change-answer="changeTargetIntent"
                     ref="intentSuggestionSelect"
      />

      <selectable-list id="selectable-intent-examples lex flex-grow"
                       v-model="selectedExamples"
                       :bot-id="botId"
                       :intent="intent"
                       :is-loading="isLoading"
                       :list="answerExamples"
                       :mode="mode"
                       class="flex flex-grow"
                       @update-mode="value => mode = value"
                       @update-example="updateExample"
      />
    </div>
  </div>
</template>

<script>
import answerExamplesService from '@/services/amio/intent-examples.service'
import intentModule from '@/store/modules/intent.module'
import IntentExamplesHeader from './IntentExamplesHeader.vue'
import SelectableList from '@/components/common/SelectableList'
import UnorderedList from '@/components/common/UnorderedList'
import WithLoader from '@/components/common/WithLoader'
import AnswerSelect from '@/components/analytics/conversations/detail/AnswerSelect.vue'
import assoc from 'ramda/src/assoc'
import dissoc from 'ramda/src/dissoc'
import head from 'ramda/src/head'
import isEmpty from 'ramda/src/isEmpty'
import sort from 'ramda/src/sort'
import { EXAMPLE_EDITOR_MODE, MIN_EXAMPLES } from '@/store/constants'
import toaster from '@/utils/toaster'
import answerModule from '@/store/modules/answer.module'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import prepend from 'ramda/src/prepend'
import update from 'ramda/src/update'
import editorUtils from '@/utils/editor.utils'
import alertTriangleIcon from '@/assets/images/icons/alert_triangle.svg'
import findIndex from 'ramda/src/findIndex'
import propEq from 'ramda/src/propEq'
import errorUtils from '@/utils/error.utils'

const EXAMPLES_STRINGS = {
  EXAMPLES_COUNT: `You need at least ${MIN_EXAMPLES} examples to publish the answer again!`,
  EXAMPLES_MOVE: 'Moving examples requires an "Intent" to be specified.'
}

export default {
  name: 'IntentExamples',
  components: { IntentExamplesHeader, SelectableList, UnorderedList, WithLoader, AnswerSelect },
  props: {
    intentId: {
      type: String,
      required: true
    },
    botId: {
      type: String,
      required: true
    }
  },

  watch: {
    intentId: {
      immediate: true,
      handler: async function (intentId) {
        await this.loadExamples(intentId)
      }
    },
    mode: {
      handler: function (newValue) {
        if (newValue === EXAMPLE_EDITOR_MODE.ADD) {
          this.$nextTick(() => {
            // nextTick must be used because in the current tick the input is not yet in HTML
            this.$refs.newExampleInput.focus()
          })
        }
        if (newValue === EXAMPLE_EDITOR_MODE.EDIT) {
          this.deselectAll()
        }
        if (newValue === EXAMPLE_EDITOR_MODE.BULK_MOVE) {
          this.targetIntent = null
          this.openIntentSuggestionSelect()
        }
      }
    },
    selectedExamples: {
      handler: function (newValue) {
        const noSelectedExamples = isEmpty(newValue)
        if (noSelectedExamples && this.mode === EXAMPLE_EDITOR_MODE.EDIT) return

        if (noSelectedExamples) {
          this.mode = EXAMPLE_EDITOR_MODE.DEFAULT
          return
        }

        this.mode = EXAMPLE_EDITOR_MODE.BULK
      }
    },
    answerExamples: {
      handler: function (newValue) {
        if (isEmpty(newValue)) {
          this.mode = EXAMPLE_EDITOR_MODE.ADD
        }
      }
    }
  },

  data() {
    return {
      answerOptions: [],
      targetIntent: null,
      isLoading: false,
      mode: EXAMPLE_EDITOR_MODE.DEFAULT,
      newExample: '',
      exampleSubmitted: false,
      selectedExamples: {},
      EXAMPLE_EDITOR_MODE,
      intentDeleteButtonId: BUSY_BUTTONS.INTENT_DELETE,
      intentMoveButtonId: BUSY_BUTTONS.INTENT_MOVE
    }
  },

  computed: {
    answer: answerModule.getAnswer,
    answerList: answerModule.getAnswerList,
    answerExamples: answerModule.getAnswerExamples,
    answerExamplesCount: answerModule.getAnswerExamplesCount,

    selectedExamplesCount() {
      return Object.keys(this.selectedExamples).length
    },

    suggestedIntent() {
      return this.targetIntent?.platformId
    },

    intent() {
      return this.answer.intent
    },

    isEmptyAnswerExamples() {
      return isEmpty(this.answerExamples)
    }
  },

  methods: {
    async loadExamples(intentId) {
      this.isLoading = true
      try {
        const answerExamples = await answerExamplesService.list(intentId)
        answerModule.setAnswerExamples(this.sortAnswerExamples(answerExamples))

        this.answerOptions = this.answerList.filter(answer => answer.id !== this.answer.id)
      } catch (e) {
        toaster.error(e)
      } finally {
        this.isLoading = false
        this.$emit('intent-examples-loaded', !this.isEmptyAnswerExamples)
      }
    },

    handleEscapeKey() {
      if (this.isEmptyAnswerExamples) return

      this.mode = EXAMPLE_EDITOR_MODE.DEFAULT
    },

    deselectAll() {
      this.selectedExamples = {}
    },

    selectAll() {
      const selectedExamples = {}
      this.answerExamples.forEach(intentExample => {
        selectedExamples[intentExample.id] = intentExample
      })
      this.selectedExamples = selectedExamples
    },

    changeTargetIntent({ intentPlatformId }) {
      if (intentPlatformId === null) {
        this.targetIntent = null
        this.openIntentSuggestionSelect()
        return
      }

      const answer = this.answerOptions.find(({ intent }) => intent.platformId === intentPlatformId)
      this.targetIntent = answer.intent
    },

    updateExample(updatedIntentExample) {
      const exampleIndex = this.answerExamples.findIndex(intentExample => intentExample.id === updatedIntentExample.id)
      const examples = update(exampleIndex, updatedIntentExample, this.answerExamples)
      answerModule.setAnswerExamples(examples)
    },

    async deleteExamples() {
      busyButtonsModule.setButtonBusy(this.intentDeleteButtonId, true)
      const totalExamplesCount = this.answerExamplesCount
      const selectedExamplesCount = this.selectedExamplesCount
      await this.checkExamplesCount(selectedExamplesCount, totalExamplesCount, this.intentDeleteButtonId)

      let isChatPreviewExampleProcessed = false
      const exampleIds = []

      Object.keys(this.selectedExamples).forEach(exampleId => {
        exampleIds.push(exampleId)
        if (this.intent.example === this.selectedExamples[exampleId].example) {
          isChatPreviewExampleProcessed = true
        }
      })

      await answerExamplesService.deleteBulk(this.intent.id, exampleIds)

      this.processExamples(isChatPreviewExampleProcessed, exampleIds)
      const toastMessage = this.resolveToastMessage(selectedExamplesCount, totalExamplesCount, 'deleted')
      toaster.success(toastMessage)
      busyButtonsModule.setButtonBusy(this.intentDeleteButtonId, false)
    },

    async checkExamplesCount(selectedExamplesCount, totalExamplesCount) {
      if (editorUtils.isUnknownAnswer(this.intent.platformId)) return
      if ((totalExamplesCount - selectedExamplesCount) >= MIN_EXAMPLES || !this.answer.published) return

      await editorUtils.updateAnswerByPublished(false, this.answer.id)
      toaster.warning(
        `
          <div class="flex items-center gap-3">
            <img src="${alertTriangleIcon}" alt="alert icon"/>
            <div class="flex flex-col items-baseline">
              <b>Answer was unpublished</b>
              ${EXAMPLES_STRINGS.EXAMPLES_COUNT}
            </div>
          </div>
        `,
        { timeout: 16000 }
      )
    },

    async moveExamples() {
      busyButtonsModule.setButtonBusy(this.intentMoveButtonId, true)
      if (this.targetIntent === null) {
        toaster.error(EXAMPLES_STRINGS.EXAMPLES_MOVE)
        return
      }

      const successMessage = this.resolveToastMessage(this.selectedExamplesCount, this.answerExamplesCount, 'moved')
      await this.checkExamplesCount(this.selectedExamplesCount, this.answerExamplesCount, this.intentMoveButtonId)

      const exampleIds = []
      let isChatPreviewExampleProcessed = false

      Object.keys(this.selectedExamples).forEach((exampleId) => {
        exampleIds.push(exampleId)
        if (this.intent.example === this.selectedExamples[exampleId].example) {
          isChatPreviewExampleProcessed = true
        }
      })

      await this.processMoveRequest(exampleIds, isChatPreviewExampleProcessed, successMessage)
      busyButtonsModule.setButtonBusy(this.intentMoveButtonId, false)
    },

    async processMoveRequest(exampleIds, isChatPreviewExampleProcessed, successMessage) {
      try {
        await answerExamplesService.moveBulk(this.targetIntent.id, Object.values(this.selectedExamples))
        this.processExamples(isChatPreviewExampleProcessed, exampleIds)
        toaster.success(successMessage)
      } catch (err) {
        const errorDescription = errorUtils.resolveMessage(err)
        toaster.error(errorDescription)
      }
    },

    processExamples(isChatPreviewExampleProcessed, processedExampleIds) {
      const examples = this.answerExamples.filter(example => !processedExampleIds.includes(example.id))
      answerModule.setAnswerExamples(examples)
      this.updateAnswerListByExamples(examples)
      if (isChatPreviewExampleProcessed) {
        const updatedIntent = this.isEmptyAnswerExamples ? dissoc('example', this.intent) : this.getIntentWithFirstExample()
        answerModule.setAnswer({ ...this.answer, intent: updatedIntent })
        intentModule.setIntent(updatedIntent)
      }
      this.selectedExamples = {}
      this.targetIntent = null
    },

    updateAnswerListByExamples(examples) {
      const answer = { ...this.answer, intent: { ...this.answer.intent, examplesCount: examples.length } }
      const index = findIndex(propEq('id', this.answer.id))(this.answerList)
      answerModule.setAnswerList(update(index, answer, this.answerList))
    },

    async submitNewExample() {
      if (this.exampleSubmitted) return

      try {
        this.exampleSubmitted = true
        const newExample = await answerExamplesService.create(this.intentId, this.newExample)
        const examples = prepend(newExample, this.answerExamples)
        answerModule.setAnswerExamples(examples)

        this.newExample = ''
        if (!this.intent.example) {
          const intentWithFirstExample = this.getIntentWithFirstExample()
          answerModule.setAnswer({ ...this.answer, intent: intentWithFirstExample })
          intentModule.setIntent(intentWithFirstExample)
        }
        this.updateAnswerListByExamples(examples)
      } catch (err) {
        this.toasterError(err)
      } finally {
        this.exampleSubmitted = false
      }
    },

    getIntentWithFirstExample() {
      return assoc('example', head(this.answerExamples).example, this.intent)
    },

    sortAnswerExamples(answerExamples) {
      return sort((a, b) => a.example.localeCompare(b.example), answerExamples)
    },

    resolveToastMessage(selectedExamplesCount, totalExamplesCount, action) {
      return `${selectedExamplesCount} ${selectedExamplesCount === 1 ? 'example' : 'examples'} ${action}`
    },

    toasterError(e) {
      const errorMessage = head(e.response.data.details.errors)?.message
      if (errorMessage.startsWith('example:')) {
        const errorMessageSplit = errorMessage.split('answer:')
        const { content, fullContent } = errorUtils.resolveDuplicateExampleMessage(errorMessageSplit[0])
        const answerId = errorMessageSplit[1].split('answerId: ')[1].replace(')', '')
        const callToAction = `already exists for answer:<br/><a target="_blank" href="/bots/${this.botId}/answers/${answerId}">${errorMessageSplit[1]}</a>`
        toaster.error(content, null, {}, fullContent, callToAction)
        return
      }

      toaster.error(errorMessage)
    },

    openIntentSuggestionSelect() {
      // nextTick must be used because in the current tick the component is not yet in HTML
      this.$nextTick(() => {
        this.$refs.intentSuggestionSelect.$refs.richSelect.showOptions()
      })
    }
  },

  async mounted() {
    this.answerOptions = this.answerList.filter(answer => answer.id !== this.answer.id)
    if (!this.isEmptyAnswerExamples) return

    this.isLoading = true
    try {
      const answerExamples = await answerExamplesService.list(this.intentId)
      answerModule.setAnswerExamples(this.sortAnswerExamples(answerExamples))
    } catch (e) {
      toaster.error(e)
    } finally {
      this.isLoading = false
      this.$emit('intent-examples-loaded', !this.isEmptyAnswerExamples)
    }
  }
}
</script>

<style lang="scss" scoped>
.intent-suggestion-examples {
  @apply sticky bg-white z-50;
  top: 60px;

  button {
    border-radius: 5px !important;
  }

  div {
    border-radius: 5px !important;
  }
}

.slide-fade-y-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-y-leave-active {
  transition: all 0.6s cubic-bezier(1, 0.5, 0.8, 1);
}

.slide-fade-y-enter-from,
.slide-fade-y-leave-to {
  transform: translateY(-15px);
  opacity: 0;
}
</style>
